import React, { useState, useEffect, useRef } from "react";
import styles from "./ChatInputArea.module.css";

interface ChatInputAreaProps {
  onSend: (question: string) => void;
  onClearChat: () => void;
  disabled?: boolean;
  placeholder?: string;
  clearOnSend?: boolean;
  hasMessages: boolean;
  isGenerating?: boolean;
  onStopGenerating?: () => void;
  expertMode: boolean;
  onExpertModeChange: (enabled: boolean) => void;
}

export const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  onSend,
  onClearChat,
  disabled,
  placeholder = "Ask about UK drone regulations...",
  clearOnSend,
  hasMessages,
  isGenerating,
  onStopGenerating,
  expertMode,
  onExpertModeChange,
}) => {
  const [question, setQuestion] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [question]);

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return;
    }

    onSend(question);

    if (clearOnSend) {
      setQuestion("");
    }
  };

  const onEnterPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      sendQuestion();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value);
  };

  const canSend = !disabled && question.trim();

  return (
    <div className={styles.wrapper}>
      {/* Stop Generating Button */}
      {isGenerating && (
        <button
          className={styles.stopButton}
          onClick={onStopGenerating}
          aria-label="Stop generating"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="14" height="14" rx="2" fill="currentColor" />
          </svg>
          <span>Stop generating</span>
        </button>
      )}

      {/* Main Input Container */}
      <div className={`${styles.container} ${isFocused ? styles.focused : ""}`}>
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          placeholder={placeholder}
          value={question}
          onChange={handleChange}
          onKeyDown={onEnterPress}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          rows={1}
          aria-label="Message input"
        />

        {/* Bottom toolbar */}
        <div className={styles.toolbar}>
          {/* Left side - tool buttons */}
          <div className={styles.toolsLeft}>
            {/* Expanded Responses Toggle */}
            <div className={styles.expertToggle}>
              <span className={styles.expertLabel}>Expanded Response</span>
              <button
                className={`${styles.toggleSwitch} ${expertMode ? styles.toggleOn : ""}`}
                onClick={() => onExpertModeChange(!expertMode)}
                role="switch"
                aria-checked={expertMode}
                aria-label="Toggle expanded responses"
                title={expertMode ? "Expanded responses: detailed responses" : "Standard mode: simpler responses"}
              >
                <span className={styles.toggleThumb} />
              </button>
            </div>
          </div>

          {/* Right side - send button */}
          <div className={styles.toolsRight}>
            {/* Character hint */}
            {question.length > 0 && (
              <span className={styles.charHint}>
                Press Enter to send
              </span>
            )}

            {/* Send button */}
            <button
              className={`${styles.sendButton} ${canSend ? styles.sendButtonEnabled : ""}`}
              onClick={sendQuestion}
              disabled={!canSend}
              aria-label="Send message"
              title="Send message"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Footer disclaimer */}
      <p className={styles.disclaimer}>
        AI responses may contain errors. Verify important regulatory information with official sources.
      </p>
    </div>
  );
};
